/**
 * Home page with unified UX pattern
 * Features: Hero section, categories with icons, featured products, feature highlights, Help CTA
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getCategories,
  getFeaturedProducts,
  getProductsSummary,
  sortProducts,
  CategoryData,
  ProductSummary,
} from '../services/staticContent';
import { HeroCarousel } from '../components/home/HeroCarousel';
import { ProductSection } from '../components/home/ProductSection';
import {
  Trophy,
  Layers,
  Target,
  Zap,
  Ticket,
  Grid2X2,
  BookOpen,
  Shield,
  CreditCard,
  ArrowRight,
  HelpCircle,
  ShoppingBag,
  AlertTriangle,
  Package,
  Clock,
  Star,
  TrendingUp,
  LucideIcon,
} from 'lucide-react';

// Map category IDs to Lucide icons
const categoryIcons: Record<string, LucideIcon> = {
  'horse-racing': Trophy,
  'card-games': Layers,
  'roulette': Target,
  'greyhound-racing': Zap,
  'lottery': Ticket,
  'multi-game': Grid2X2,
};

export default function HomePage(): JSX.Element {
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<ProductSummary[]>([]);
  const [allProducts, setAllProducts] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStaticContent();
  }, []);

  async function loadStaticContent(): Promise<void> {
    try {
      const [categoriesData, featuredData, productsData] = await Promise.all([
        getCategories(),
        getFeaturedProducts(),
        getProductsSummary(),
      ]);
      setCategories(categoriesData);
      setFeaturedProducts(featuredData);
      setAllProducts(productsData);
    } catch (error) {
      console.error('Failed to load static content:', error);
      // Fallback to hardcoded categories if fetch fails
      setCategories(fallbackCategories);
    } finally {
      setLoading(false);
    }
  }

  // Computed product lists for sections
  const newArrivals = useMemo(() => {
    return sortProducts(allProducts, 'publishedAt', 'desc').slice(0, 8);
  }, [allProducts]);

  const topRated = useMemo(() => {
    // Filter premium products as "top rated"
    const premium = allProducts.filter(p => p.qualityTier === 'premium');
    return premium.length > 0 ? premium.slice(0, 8) : allProducts.slice(0, 8);
  }, [allProducts]);

  const popular = useMemo(() => {
    // Use price as a proxy for popularity (higher priced = more popular assumption)
    return sortProducts(allProducts, 'price', 'desc').slice(0, 8);
  }, [allProducts]);

  return (
    <div>
      {/* Hero Carousel - Featured Products */}
      <HeroCarousel products={featuredProducts} autoPlayInterval={6000} />

      {/* Disclaimer */}
      <section className="bg-amber-50 dark:bg-amber-900/20 border-y border-amber-200 dark:border-amber-700 py-4">
        <div className="container-custom">
          <div className="flex items-center justify-center gap-3">
            <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <p className="text-center text-sm text-amber-800 dark:text-amber-300">
              <strong>Important:</strong> All content is for educational and entertainment purposes
              only. No guarantees of winning or financial success are made or implied.
            </p>
          </div>
        </div>
      </section>


      {/* Categories */}
      <section className="py-16">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Popular Categories
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Explore our comprehensive collection of strategy guides across multiple gaming categories
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card p-6 animate-pulse">
                  <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-xl mx-auto mb-4" />
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto mb-2" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-1" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mx-auto" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => {
                const IconComponent = categoryIcons[category.id] || Package;
                return (
                  <Link
                    key={category.id}
                    to={`/products?category=${category.id}`}
                    className="card p-6 text-center group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary-200 dark:group-hover:bg-primary-800/50 transition-colors">
                      <IconComponent size={32} className="text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      {category.description}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      Browse Guides
                      <ArrowRight size={14} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Product Sections */}
      <div className="bg-gray-50 dark:bg-gray-900/50">
        {/* New Arrivals */}
        <ProductSection
          title="New Arrivals"
          subtitle="Fresh strategy guides just added to our collection"
          icon={Clock}
          products={newArrivals}
          viewAllLink="/products"
          viewAllParams="sort=newest"
          loading={loading}
          accentColor="green"
        />

        {/* Top Rated */}
        <ProductSection
          title="Top Rated"
          subtitle="Our highest quality premium guides"
          icon={Star}
          products={topRated}
          viewAllLink="/products"
          viewAllParams="tier=premium"
          loading={loading}
          accentColor="amber"
        />

        {/* Most Popular */}
        <ProductSection
          title="Most Popular"
          subtitle="Best-selling guides loved by our customers"
          icon={TrendingUp}
          products={popular}
          viewAllLink="/products"
          viewAllParams="sort=popular"
          loading={loading}
          accentColor="blue"
        />
      </div>

      {/* Features */}
      <section className="py-16">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Why Choose Foxie Systems?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              We provide high-quality educational content with a seamless purchasing experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mx-auto mb-6">
                <BookOpen size={32} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                Expert Content
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Comprehensive strategies developed through extensive analysis and research by industry experts.
              </p>
            </div>

            <div className="card p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Shield size={32} className="text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                Secure Downloads
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Instant access to your purchases with secure, time-limited download links and SSL encryption.
              </p>
            </div>

            <div className="card p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-6">
                <CreditCard size={32} className="text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                One-Time Purchase
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Own your guides forever with no subscriptions or recurring fees. Pay once, access forever.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="py-16">
        <div className="container-custom">
          <div className="card p-12 bg-gradient-to-br from-primary-600 to-primary-800 text-white text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Browse our collection of educational gambling strategy guides and start learning today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/products"
                className="btn bg-white text-primary-800 hover:bg-gray-100 inline-flex items-center justify-center gap-2 text-lg px-8 py-3"
              >
                <ShoppingBag size={20} />
                View All Products
              </Link>
              <Link
                to="/help"
                className="btn bg-transparent border-2 border-white hover:bg-white hover:text-primary-800 inline-flex items-center justify-center gap-2 text-lg px-8 py-3 transition-all"
              >
                <HelpCircle size={20} />
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Help CTA */}
      <section className="py-12 bg-gray-50 dark:bg-gray-900">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-primary-100 dark:border-primary-800">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-sm">
                  <HelpCircle size={24} className="text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Have questions about our products?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Visit our help center for FAQs, buying guides, and support
                  </p>
                </div>
              </div>
              <Link to="/help" className="btn btn-primary whitespace-nowrap inline-flex items-center gap-2">
                <HelpCircle size={16} />
                Visit Help Center
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Fallback category data (used if static content fetch fails)
const fallbackCategories: CategoryData[] = [
  {
    id: 'horse-racing',
    name: 'Horse Racing',
    description: 'Expert strategies for horse racing betting',
    icon: '🏇',
  },
  {
    id: 'card-games',
    name: 'Card Games',
    description: 'Poker, Blackjack, and more',
    icon: '🃏',
  },
  {
    id: 'roulette',
    name: 'Roulette',
    description: 'Betting strategies for roulette',
    icon: '🎰',
  },
  {
    id: 'greyhound-racing',
    name: 'Greyhound Racing',
    description: 'Winning systems for greyhound racing',
    icon: '🐕',
  },
  {
    id: 'lottery',
    name: 'Lottery Systems',
    description: 'Number selection strategies',
    icon: '🎱',
  },
  {
    id: 'multi-game',
    name: 'Multi-Game',
    description: 'Cross-category betting bundles',
    icon: '🎯',
  },
];
